import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import { ProductSortType } from "src/types/GlobalInterfaces";
import qs from "qs";
import {
  convertToApiSortKey,
  getHeaderHeight,
  getLoginUserID,
} from "src/helpers/helperFunctions";
import HomcareProduct from "src/Components/WebsiteComponents/HomePage/Cards/HomcareProduct";
import ProductsViewContainer from "src/Components/Pharmacy/ProductsViewContainer";
import PriceRangeSelector from "src/Components/Pharmacy/PriceRangeSelector";
import SideTabList from "src/Components/Pharmacy/SideTabList";
import { QueryPath } from "src/lib/constans";
import { IPaginated } from "src/types/paginated";
import { useQueryParams } from "src/hooks/useQueryParams";
import { capitalize } from "@mui/material";
import { removeAllDisLike } from "src/Store/disLike";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/Store/store";

const ProductByHomeCarePage = () => {
  const [categoryLoader, setCategoryLoader] = useState<boolean>(false);
  const [categoryProducts, setCategoryProducts] = useState<any[]>([]);
  const [allCategories, setAllCategories] = useState<any[]>([]);
  const [allCategoriesLoader, setAllCategoriesLoader] = useState<boolean>(false);
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  });

  const searchParams = useQueryParams();
  const categoryIds = searchParams.getParam(QueryPath.homeCareCategory) || "";
  const search = searchParams.getParam(QueryPath.search) || "";
  const categoryName = searchParams.getParam(QueryPath.homeCareCategoryName) || "";

  const navigate = useNavigate();
  const { homecareDisLike } = useSelector((state: RootState) => state.disLike);
  const dispatch = useDispatch<AppDispatch>();

  const [queryParams, setQueryParams] = useState<{
    category: string[];
    page: number;
    limit: number;
    sortBy: ProductSortType;
    minPrice: number;
    maxPrice: number;
    search: string;
  }>({
    category: categoryIds ? categoryIds.split(",") : [],
    page: 1,
    limit: 12,
    sortBy: "most-popular",
    minPrice: 0,
    maxPrice: 100000,
    search: search || "",
  });

  const [bannerData, setBannerData] = useState<any>(null);
  const [bannerDataLoading, setBannerDataLoading] = useState<boolean>(false);

  // === DEBOUNCE REFS ===
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchDebounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const debounceDelay = 500; // Filters
  const searchDebounceDelay = 800; // Search

  // === BANNER ===
  const getBannerData = async (categoryId: string) => {
    setBannerDataLoading(true);
    const { res, err } = await httpRequest<any>({
      path: `/homecare-category/${categoryId}`,
    });
    if (res) {
      setBannerData({
        description: res.categoryDescription || "",
        image: res.categoryBanner || "",
        logo: res.categoryImage || "",
        title: res.categoryName || "",
      });
    } else {
      toast(err, { type: "error" });
    }
    setBannerDataLoading(false);
  };

  // === FETCH PRODUCTS ===
  const fetchCategoryProducts = async (
    params: Partial<typeof queryParams>,
    isLoadMore = false
  ) => {
    const q = {
      ids: params.category || "",
      page: params.page,
      limit: params.limit,
      sortBy: convertToApiSortKey(params.sortBy || "most-popular"),
      userId: getLoginUserID(),
      minPrice: params.minPrice,
      maxPrice: params.maxPrice,
      search: params.search,
    };

    if (params.page === 1) {
      setCategoryProducts([]);
    }

    if (params?.category?.length === 1 && params?.category[0]) {
      getBannerData(params.category[0]);
    } else {
      setBannerData({ description: "", image: "", logo: "", title: "" });
    }

    setCategoryLoader(true);
    const { res, err } = await httpRequest<{
      documents: any[];
      paginated: IPaginated;
    }>({
      path: `/home-care-services/home/filter-product?${qs.stringify(q)}`,
    });

    if (res) {
      if (isLoadMore) {
        setCategoryProducts((prev) => {
          const existingIds = new Set(prev.map((p) => p._id));
          const newData = res.documents.filter((p) => !existingIds.has(p._id));
          return [...prev, ...newData];
        });
      } else {
        setCategoryProducts(res.documents);
      }
      setPaginated(res.paginated);
      dispatch(removeAllDisLike("homecareDisLike"));
    } else {
      toast(err, { type: "error" });
    }
    setCategoryLoader(false);
  };

  // === CHANGE QUERY PARAMS ===
  const changeQueryParams = (
    params: Partial<typeof queryParams>,
    update: boolean = true,
    reset: boolean = true,
    isSearch: boolean = false
  ) => {
    let qq = { ...queryParams, ...params };
    setQueryParams(qq);
    if (reset) {
      qq.page = 1;
    }
    if (update) {
      window.scrollTo(0, 0);
      setCategoryLoader(true);
      if (isSearch) {
        debouncedSearchProducts(qq);
      } else {
        debouncedFetchProducts(qq);
      }
    }
  };

  // === DEBOUNCED FETCH ===
  const debouncedFetchProducts = useCallback((params: any) => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => {
      fetchCategoryProducts(params, false);
    }, debounceDelay);
  }, []);

  const debouncedSearchProducts = useCallback((params: any) => {
    if (searchDebounceTimeoutRef.current) clearTimeout(searchDebounceTimeoutRef.current);
    searchDebounceTimeoutRef.current = setTimeout(() => {
      fetchCategoryProducts(params, false);
    }, searchDebounceDelay);
  }, []);

  // === LOAD CATEGORIES ===
  const getAllCategories = async () => {
    setAllCategoriesLoader(true);
    const { res, err } = await httpRequest<{ documents: any[] }>({
      path: "/homecare-category/home/get-top-categories",
    });
    if (res) {
      setAllCategories(
        res.documents.map((item) => ({
          label: item.categoryName,
          value: item.id,
          id: item._id,
        }))
      );
    } else {
      toast(err, { type: "error" });
    }
    setAllCategoriesLoader(false);
  };

  useEffect(() => {
    getAllCategories();
  }, []);

  // === CLEANUP ===
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
      if (searchDebounceTimeoutRef.current) clearTimeout(searchDebounceTimeoutRef.current);
    };
  }, []);

  // === URL → STATE SYNC ===
  useEffect(() => {
    const isSearchChange = search !== queryParams.search;
    changeQueryParams(
      {
        category: categoryIds ? categoryIds.split(",") : [],
        page: 1,
        search: search || "",
      },
      true,
      true,
      isSearchChange
    );
  }, [categoryIds, search]);

  // === DISLIKE UPDATE ===
  useEffect(() => {
    if (homecareDisLike.length > 0) {
      setCategoryProducts((prev) =>
        prev.map((v) => ({
          ...v,
          isLiked: homecareDisLike.includes(v._id) ? false : v.isLiked,
        }))
      );
      dispatch(removeAllDisLike("homecareDisLike"));
    }
  }, [homecareDisLike, dispatch]);

  return (
    <div className="w-full bg-banner-white-image">
      <div className="view-area-width flex gap-1 py-3 relative">
        {/* Sidebar */}
        <div
          className="p-[2px] product-side-bar-width custom-ai-scroll-bar !sticky overflow-y-auto"
          style={{
            height: `calc(100vh - ${getHeaderHeight()}px - 16px)`,
            top: `${getHeaderHeight() + 12}px`,
          }}
        >
          <div className="w-full box-design">
            <SideTabList
              title={`Categories ${allCategories.length > 0 ? `(${allCategories.length})` : ""}`}
              List={allCategories}
              onListClick={(item) => {
                const current = categoryIds ? categoryIds.split(",") : [];
                const isExist = current.includes(item.id);
                const newData = isExist
                  ? current.filter((id) => id !== item.id)
                  : [...current, item.id];

                const q: any = {};
                if (newData.length > 0) {
                  q[QueryPath.homeCareCategory] = allCategories
                    .filter((c) => newData.includes(c.id))
                    .map((c) => c.id)
                    .join(",");
                  q[QueryPath.homeCareCategoryName] = allCategories
                    .filter((c) => newData.includes(c.id))
                    .map((c) => c.label)
                    .join(",");
                }
                navigate(`/home-care?${qs.stringify(q)}`);
              }}
              showCheckBox={true}
              showRadio={false}
              loading={allCategoriesLoader}
              loadingText="Loading Home Care Categories"
              selectedItem={
                allCategories
                  .filter((c) => categoryIds.split(",").includes(c.id))
                  ?.map((c) => c.id) || []
              }
            />

            <PriceRangeSelector
              min={queryParams.minPrice}
              max={queryParams.maxPrice}
              onChange={(min, max) => {
                changeQueryParams({ minPrice: min, maxPrice: max });
              }}
            />
          </div>
        </div>

        {/* Products View */}
        <ProductsViewContainer
          breadCrumbData={[
            { label: "Home" },
            {
              label: "Home Care",
              onClick: () => navigate("/view-home-care"),
            },
            ...(categoryName ? [{ label: categoryName.split(",").join(" , ") }] : []),
            ...(search ? [{ label: search }] : []),
          ]}
          heading="Home Care"
          loading={categoryLoader}
          renderSkeleton={() => <HomcareProduct skeleton={true} data={{}} />}
          bannerData={bannerData}
          bannerLoading={bannerDataLoading}
          sortOptions={[
            { label: "Most Popular", value: "most-popular" },
            { label: "Most Rated", value: "most-rated" },
            { label: "Low to High", value: "price-low-to-high" },
            { label: "High to Low", value: "price-high-to-low" },
          ]}
          currentSort={queryParams.sortBy}
          onSortChange={(sortBy) => {
            changeQueryParams({ sortBy: sortBy as ProductSortType });
          }}
          products={categoryProducts}
          renderProduct={(data, index) => (
            <HomcareProduct
              key={data._id}
              data={data}
              index={index}
              onLikeProduct={(id, isLiked) => {
                setCategoryProducts((prev) =>
                  prev.map((item) =>
                    item._id === id ? { ...item, isLiked } : item
                  )
                );
              }}
            />
          )}
          currentPage={queryParams.page}
          totalPages={paginated.totalPages}
          onLoadMore={(page) => {
            const updatedParams = { ...queryParams, page };
            setQueryParams(updatedParams);
            fetchCategoryProducts(updatedParams, true);
          }}
          onLoadAgain={() => {
            changeQueryParams({});
          }}
        />
      </div>
    </div>
  );
};

export default ProductByHomeCarePage;